// src/App.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { BarChart2, DollarSign, Headset, Paintbrush, Code, Info, Users, School, Home, Mail, Phone, MessageSquare, Smartphone, MapPin, Send, CheckCircle, XCircle, Linkedin, Facebook, Instagram, Menu, X, ArrowRight } from 'lucide-react';

// --- Import ALL your page components from src/pages ---
import GraphicsDesignPage from './pages/GraphicsDesign.js';
import PosterGalleryPage from './pages/PosterGallery.js';
import VideoEditingPage from './pages/VideoEditing.js';
import OtherPortfolioPage from './pages/OtherPortfolio.js';
import BackgroundRemoverPage from './pages/backgroundRemover.js';

// Other main pages
import DataAnalysisPage from './pages/DataAnalysis.js';
import AccountingBookkeepingPage from './pages/AccountingBookkeeping.js';
import VirtualAssistancePage from './pages/VirtualAssistance.js';
import KidsHubPage from './pages/KidsHub.js';
import AboutUsPage from './pages/AboutUs.js';
import ContactUsPage from './pages/ContactUs.js';
import PrivacyPolicyPage from './pages/PrivacyPolicy.js';
import TermsOfServicePage from './pages/TermsOfService.js';
import SitemapPage from './pages/Sitemap.js';
import GetAQuotePage from './pages/GetAQuotePage.js';
import AccountingCalculatorPage from './pages/AccountingCalculatorPage.js';

// --- Service Card Component for the Home Page ---
const ServiceCard = ({ icon: Icon, title, description, delay, path }) => {
    const [isVisible, setIsVisible] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { root: null, rootMargin: '0px', threshold: 0.2 }
        );
        if (cardRef.current) {
            observer.observe(cardRef.current);
        }
        return () => {
            if (cardRef.current) {
                observer.unobserve(cardRef.current);
            }
        };
    }, []);

    return (
        <Link to={path} className="block">
            <div
                ref={cardRef}
                className={`
                    bg-[#0A1128] border border-[#C9B072] rounded-xl p-8 flex flex-col items-center text-center shadow-lg
                    transition-all duration-700 ease-out transform
                    ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                    hover:scale-105 hover:shadow-2xl
                `}
                style={{ transitionDelay: `${delay}ms` }}
            >
                <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                    <Icon size={48} />
                </div>
                <h3 className="text-2xl font-bold text-[#F8F8F8] mb-3">{title}</h3>
                <p className="text-[#CCD2E3] text-lg leading-relaxed">{description}</p>
                <div className="mt-6 px-6 py-3 bg-[#4CAF50] text-[#F8F8F8] font-semibold rounded-full hover:bg-opacity-90 transition duration-300 transform hover:scale-105 shadow-md">
                    Learn More
                </div>
            </div>
        </Link>
    );
};

// --- Google Analytics Tracker Component ---
const GaTracker = () => {
    const location = useLocation();
    useEffect(() => {
        if (window.gtag) {
            window.gtag('event', 'page_view', {
                page_path: location.pathname + location.search,
                page_location: window.location.href,
                page_title: document.title,
            });
            console.log('GA Page View:', location.pathname + location.search);
        }
    }, [location]);
    return null;
};

// --- Updated Header Component with Dropdown ---
const Header = ({ mobileMenuOpen, toggleMobileMenu }) => {
    const location = useLocation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Main navigation links
    const navLinks = [
        { name: 'Home', path: '/', icon: <Home size={20} /> },
        { name: 'About', path: '/about-us', icon: <Info size={20} /> },
        { name: 'Contact', path: '/contact-us', icon: <Mail size={20} /> },
    ];

    // Services dropdown links
    const serviceLinks = [
        { name: 'Graphics & Design', path: '/graphics-design' },
        { name: 'Data Analysis', path: '/data-analysis' },
        { name: 'Accounting & Bookkeeping', path: '/accounting-bookkeeping' },
        { name: 'Virtual Assistance', path: '/virtual-assistance' },
    ];

    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
    const closeDropdown = () => setIsDropdownOpen(false);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                closeDropdown();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    return (
        <header className="fixed top-0 left-0 w-full z-50 bg-[#0A1128]/60 backdrop-blur-xl border-b border-gray-700 transition-all duration-300">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
                {/* Text logo */}
                <Link to="/" className="flex items-center space-x-2">
                    <span className="text-xl md:text-2xl font-bold text-[#C9B072] transition-colors duration-300">
                        JAKOM: Your One-Stop Tech Solution
                    </span>
                </Link>

                {/* Desktop Navigation Menu */}
                <nav className="hidden lg:flex items-center space-x-8">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            className={`flex items-center space-x-2 text-base font-medium px-3 py-2 rounded-lg transition-all duration-300
                            ${location.pathname.startsWith(link.path) && link.path !== '/'
                                ? 'text-[#C9B072]'
                                : location.pathname === '/' && link.path === '/'
                                    ? 'text-[#C9B072]'
                                    : 'text-[#CCD2E3] hover:text-[#C9B072]'
                            }
                            `}
                        >
                            <span>{link.name}</span>
                        </Link>
                    ))}
                    {/* Services Dropdown */}
                    <div ref={dropdownRef} className="relative group">
                        <button
                            onClick={toggleDropdown}
                            className={`flex items-center space-x-2 text-base font-medium px-3 py-2 rounded-lg transition-all duration-300
                            ${location.pathname.startsWith('/graphics-design') || location.pathname.startsWith('/data-analysis') || location.pathname.startsWith('/accounting-bookkeeping') || location.pathname.startsWith('/virtual-assistance')
                                ? 'text-[#C9B072]'
                                : 'text-[#CCD2E3] hover:text-[#C9B072]'
                            }
                            `}
                        >
                            <span>Services</span>
                            <Paintbrush size={20} />
                        </button>
                        {isDropdownOpen && (
                            <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-[#0A1128] ring-1 ring-[#C9B072] z-50">
                                <div className="py-1">
                                    {serviceLinks.map((service) => (
                                        <Link
                                            key={service.name}
                                            to={service.path}
                                            className="block px-4 py-2 text-sm text-[#CCD2E3] hover:bg-[#1A2A4C] hover:text-[#C9B072]"
                                            onClick={closeDropdown}
                                        >
                                            {service.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <Link
                        to="/get-a-quote"
                        className="bg-[#C9B072] text-[#0A1128] font-bold py-2 px-6 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 hover:bg-[#DBC390]"
                    >
                        Get a Quote
                    </Link>
                </nav>

                {/* Mobile Menu Button */}
                <div className="lg:hidden flex items-center">
                    <button onClick={toggleMobileMenu} className="p-2 rounded-md text-[#C9B072] hover:bg-[#1A2A4C] transition duration-300">
                        {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <div className={`lg:hidden fixed inset-0 bg-[#0A1128]/95 backdrop-blur-md z-40 transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex flex-col items-start p-8 space-y-6 pt-20">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            onClick={toggleMobileMenu}
                            className={`flex items-center space-x-4 w-full text-2xl font-semibold py-4 rounded-lg
                            ${location.pathname.startsWith(link.path) && link.path !== '/'
                                ? 'text-[#C9B072] border-l-4 border-[#C9B072] pl-4'
                                : location.pathname === '/' && link.path === '/'
                                    ? 'text-[#C9B072] border-l-4 border-[#C9B072] pl-4'
                                    : 'text-[#F8F8F8] hover:text-[#C9B072]'
                            }
                            `}
                        >
                            {link.icon}
                            <span>{link.name}</span>
                        </Link>
                    ))}
                    {/* Services Dropdown in Mobile Menu */}
                    <div className="w-full">
                        <button
                            onClick={toggleDropdown}
                            className={`flex items-center space-x-4 w-full text-2xl font-semibold py-4 rounded-lg
                            ${isDropdownOpen ? 'text-[#C9B072]' : 'text-[#F8F8F8] hover:text-[#C9B072]'}
                            `}
                        >
                            <Paintbrush size={20} />
                            <span>Services</span>
                        </button>
                        {isDropdownOpen && (
                            <div className="pl-8 pt-2 space-y-2">
                                {serviceLinks.map((service) => (
                                    <Link
                                        key={service.name}
                                        to={service.path}
                                        onClick={() => {
                                            closeDropdown();
                                            toggleMobileMenu();
                                        }}
                                        className="block text-xl text-[#F8F8F8] hover:text-[#C9B072]"
                                    >
                                        {service.name}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                    <Link
                        to="/get-a-quote"
                        onClick={toggleMobileMenu}
                        className="w-full text-center bg-[#C9B072] text-[#0A1128] font-bold py-3 px-6 rounded-full shadow-lg transition-all duration-300 mt-8 hover:bg-[#DBC390]"
                    >
                        Get a Quote
                    </Link>
                </div>
            </div>
        </header>
    );
};

// --- New `FutureSection` for the Hero/Landing Page ---
const FutureSection = () => (
    <section className="relative h-[90vh] md:h-screen w-full bg-[#F8F8F8] text-[#0A1128] flex flex-col md:flex-row items-center justify-center px-4 overflow-hidden">
        {/* Hero Content - Left Side */}
        <div className="relative z-10 w-full md:w-1/2 lg:w-2/5 flex flex-col items-start text-left px-4 md:px-12 py-10">
            <div className="flex items-center mb-2">
                <span className="text-sm font-bold text-[#C9B072] mr-2 tracking-widest uppercase">
                    Here and Now
                </span>
                <span className="w-10 h-0.5 bg-[#555]"></span>
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black uppercase mb-4 tracking-tight leading-none">
                <span className="block text-[#0A1128]">JAKOM</span>
            </h1>
            <p className="max-w-md text-[#333] text-lg font-bold leading-relaxed mb-8">
                Empowering Your Business with Seamless Integration and Expert Support.
            </p>
            <Link
                to="/get-a-quote"
                className="inline-flex items-center px-6 py-3 border border-[#C9B072] bg-[#C9B072] text-[#0A1128] font-semibold text-sm uppercase tracking-wider rounded-full transition-all duration-300 transform hover:scale-105"
            >
                Let's Go
                <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
        </div>

        {/* Right Side with JAKOM Logo */}
        <div className="relative z-10 w-full md:w-1/2 lg:w-3/5 flex items-center justify-center py-10">
            <img
                src="https://i.imgur.com/GnYpS80.png"
                alt="Abstract futuristic figure"
                className="max-w-sm max-h-[50vh] object-contain transition-transform duration-500 ease-in-out hover:scale-105"
            />
        </div>
    </section>
);


// Main App Component
const App = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const observedSections = useRef(new Set());
    const setSectionRef = useCallback((node) => {
        if (node) {
            observedSections.current.add(node);
        }
    }, []);

    const handleAppSectionIntersect = useCallback((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up-active');
                if (entry.target.currentObserver) {
                    entry.target.currentObserver.unobserve(entry.target);
                }
            }
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleAppSectionIntersect, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        });
        observedSections.current.forEach(section => {
            if (section) {
                observer.observe(section);
                section.currentObserver = observer;
            }
        });
        return () => {
            observedSections.current.forEach(section => {
                if (section && section.currentObserver) {
                    section.currentObserver.unobserve(section);
                }
            });
            observer.disconnect();
            observedSections.current.clear();
        };
    }, [handleAppSectionIntersect]);

    return (
        <Router>
            <GaTracker />
            <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

                <style>
                    {`
                    html, body { height: 100%; }
                    body { font-family: 'Inter', sans-serif; margin: 0; background-color: #0A1128; color: #F8F8F8; overflow-x: hidden; }

                    /* New animations and styles for the modern design */
                    .animate-pulse-slow {
                        animation: pulse-slow 20s infinite ease-in-out;
                    }

                    @keyframes pulse-slow {
                        0%, 100% { transform: scale(1); opacity: 0.1; }
                        50% { transform: scale(1.05); opacity: 0.15; }
                    }

                    .fade-in-up { opacity: 0; transform: translateY(20px); transition: opacity 0.8s ease-out, transform 0.8s ease-out; }
                    .fade-in-up-active { opacity: 1; transform: translateY(0); }
                    `}
                </style>

                {/* Header Section (remains outside of Routes as it's common to all pages) */}
                <Header mobileMenuOpen={mobileMenuOpen} toggleMobileMenu={toggleMobileMenu} />

                <main className=""> {/* Removed pt-20 as the hero section is now full height */}
                    {/* Define Routes for different pages. The content within <Routes> will change based on the URL. */}
                    <Routes>
                        {/* Home Page Route - Renders all original sections of the home page */}
                        <Route path="/" element={
                            <> {/* Use a React Fragment to group multiple elements for the home page route */}
                                <FutureSection /> {/* This replaces the old hero section */}

                                <section className="py-20 bg-[#0A1128] container mx-auto px-6">
                                    <h2 className="text-5xl font-extrabold text-center text-[#F8F8F8] mb-16 fade-in-up" ref={setSectionRef}>
                                        Our Integrated Services
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                                        <ServiceCard icon={Paintbrush} title="Graphics & Design" description="Stunning visuals that capture attention and communicate your brand's unique story effectively." delay={0} path="/graphics-design" />
                                        <ServiceCard icon={BarChart2} title="Data Analysis" description="Unlock actionable insights from your data to drive informed decisions and strategic growth." delay={100} path="/data-analysis" />
                                        <ServiceCard icon={DollarSign} title="Accounting & Bookkeeping" description="Expert financial management to ensure accuracy, compliance, and peace of mind." delay={200} path="/accounting-bookkeeping" />
                                        <ServiceCard icon={Headset} title="Virtual Assistance" description="Efficient administrative support, freeing your time to focus on core business activities." delay={300} path="/virtual-assistance" />
                                    </div>
                                </section>

                                <section className="py-20 bg-gray-100 border-t border-[#C9B072]">
                                    <div className="container mx-auto flex flex-col md:flex-row items-center gap-12 px-6">
                                        <div className="md:w-1/2 flex justify-center fade-in-up" ref={setSectionRef}>
                                            <img
                                                src={process.env.PUBLIC_URL + '/images/kidshub.png'}
                                                alt="Kids Hub"
                                                className="rounded-xl shadow-2xl object-cover"
                                                loading="lazy"
                                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/500x350/0A1128/4CAF50?text=Kids+Hub+Fun'; }}
                                            />
                                        </div>
                                        <div className="md:w-1/2 text-center md:text-left fade-in-up" ref={setSectionRef}>
                                            <h2 className="text-5xl font-extrabold text-[#0A1128] mb-6">
                                                Empowering the Next Generation: <span className="text-[#4CAF50]">Kids Hub</span>
                                            </h2>
                                            <p className="text-xl text-[#333333] leading-relaxed mb-8">
                                                At JAKOM, we believe in nurturing talent from a young age. Our Kids Hub offers engaging and interactive programs designed to introduce children to the exciting world of technology, creativity, and problem-solving. Spark curiosity and build foundational skills for a brighter future!
                                            </p>
                                            <Link to="/kids-hub" className="px-8 py-3 bg-[#C9B072] text-[#0A1128] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                                                Explore Kids Hub
                                            </Link>
                                        </div>
                                    </div>
                                </section>

                                <section className="py-20 bg-[#0A1128] border-t border-[#C9B072] container mx-auto px-6">
                                    <div className="max-w-3xl mx-auto text-center">
                                        <h2 className="text-5xl font-extrabold text-[#F8F8F8] mb-6 fade-in-up" ref={setSectionRef}>
                                            About JAKOM
                                        </h2>
                                        <p className="text-xl text-[#CCD2E3] leading-relaxed mb-8 fade-in-up" ref={setSectionRef}>
                                            JAKOM is more than just a service provider; we are your dedicated partner in navigating the complexities of modern business. Our mission is to simplify operations, enhance efficiency, and foster growth for enterprises of all sizes through innovative tech solutions and unparalleled expertise. We pride ourselves on delivering integrated services that truly make a difference.
                                        </p>
                                        <Link to="/about-us" className="px-8 py-3 bg-[#4CAF50] text-[#F8F8F8] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                                            Read Our Full Story
                                        </Link>
                                    </div>
                                </section>

                                <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                                    <h2 className="text-5xl font-extrabold text-[#0A1128] mb-6 fade-in-up" ref={setSectionRef}>
                                        Ready to Elevate Your Business?
                                    </h2>
                                    <p className="text-xl text-[#333333] mb-10 fade-in-up" ref={setSectionRef}>
                                        Let's discuss how JAKOM's comprehensive solutions can empower your success.
                                    </p>
                                    <Link to="/contact-us" className="px-12 py-4 bg-[#C9B072] text-[#0A1128] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 animate-pulse fade-in-up inline-flex items-center justify-content-center" ref={setSectionRef}>
                                        Contact Us Now
                                    </Link>
                                </section>
                            </>
                        } />

                        {/* Routes for all main pages */}
                        <Route path="/graphics-design" element={<GraphicsDesignPage />} />
                        <Route path="/data-analysis" element={<DataAnalysisPage />} />
                        <Route path="/accounting-bookkeeping" element={<AccountingBookkeepingPage />} />
                        <Route path="/virtual-assistance" element={<VirtualAssistancePage />} />
                        <Route path="/kids-hub" element={<KidsHubPage />} />
                        <Route path="/about-us" element={<AboutUsPage />} />
                        <Route path="/contact-us" element={<ContactUsPage />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                        <Route path="/sitemap" element={<SitemapPage />} />
                        <Route path="/get-a-quote" element={<GetAQuotePage />} />
                        <Route path="/accounting-bookkeeping/ratio-calculator" element={<AccountingCalculatorPage />} />

                        {/* Routes for the Graphics & Design sub-pages (now full pages) */}
                        <Route path="/graphics-design/poster-gallery" element={<PosterGalleryPage />} />
                        <Route path="/graphics-design/video-editing" element={<VideoEditingPage />} />
                        <Route path="/graphics-design/other-portfolio" element={<OtherPortfolioPage />} />
                        <Route path="/graphics-design/image-tools" element={<BackgroundRemoverPage />} />

                    </Routes>
                </main>

                {/* Footer (remains outside of Routes as it's common to all pages) */}
                <footer className="bg-[#0A1128] border-t border-[#C9B072] py-12 text-[#CCD2E3]">
                    <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-8 text-center md:text-left">
                        {/* Section 1: Company Info & Logo */}
                        <div className="col-span-1">
                            <Link to="/" className="inline-block mb-4">
                                <img src="https://i.imgur.com/zWVSml6.png" alt="JAKOM Logo" className="h-10 w-auto object-contain mx-auto md:mx-0" />
                            </Link>
                            <p className="text-sm leading-relaxed mt-2">
                                Empowering your business with seamless integration and expert support.
                            </p>
                            <p className="text-sm mt-4">&copy; {new Date().getFullYear()} JAKOM. All rights reserved.</p>
                            <p className="mt-2 text-xs">Built by Obare Emmanuel</p>
                        </div>

                        {/* Section 2: Quick Links */}
                        <div className="col-span-1">
                            <h3 className="text-xl font-bold text-[#F8F8F8] mb-4">Quick Links</h3>
                            <ul className="space-y-2">
                                <li><Link to="/about-us" className="hover:text-[#C9B072] transition duration-300">About Us</Link></li>
                                <li><Link to="/contact-us" className="hover:text-[#C9B072] transition duration-300">Contact Us</Link></li>
                                <li><Link to="/graphics-design" className="hover:text-[#C9B072] transition duration-300">Our Services</Link></li>
                                <li><Link to="/kids-hub" className="hover:text-[#C9B072] transition duration-300">Kids Hub</Link></li>
                                <li><Link to="/sitemap" className="hover:text-[#C9B072] transition duration-300">Sitemap</Link></li>
                                <li><Link to="/get-a-quote" className="hover:text-[#C9B072] transition duration-300">Get a Quote</Link></li>
                            </ul>
                        </div>

                        {/* Section 3: Legal & Resources */}
                        <div className="col-span-1">
                            <h3 className="text-xl font-bold text-[#F8F8F8] mb-4">Legal & Resources</h3>
                            <ul className="space-y-2">
                                <li><Link to="/privacy-policy" className="hover:text-[#C9B072] transition duration-300">Privacy Policy</Link></li>
                                <li><Link to="/terms-of-service" className="hover:text-[#C9B072] transition duration-300">Terms of Service</Link></li>
                                <li><a href="#" className="hover:text-[#C9B072] transition duration-300">FAQ</a></li>
                                <li><a href="#" className="hover:text-[#C9B072] transition duration-300">Support</a></li>
                            </ul>
                        </div>

                        {/* Section 4: Contact Information & Social Media */}
                        <div className="col-span-1">
                            <h3 className="text-xl font-bold text-[#F8F8F8] mb-4">Get In Touch</h3>
                            <div className="space-y-3 mb-6">
                                <p className="flex items-center justify-center md:justify-start">
                                    <MapPin size={20} className="mr-3 text-[#C9B072]" />
                                    <span>Nairobi, Kenya</span>
                                </p>
                                <p className="flex items-center justify-center md:justify-start">
                                    <Mail size={20} className="mr-3 text-[#C9B072]" />
                                    <a href="mailto:emmanuelomondiobare@gmail.com" className="hover:text-[#C9B072] transition duration-300">emmanuelomondiobare@gmail.com</a>
                                </p>
                                <p className="flex items-center justify-center md:justify-start">
                                    <Phone size={20} className="mr-3 text-[#C9B072]" />
                                    <a href="tel:+254794255000" className="hover:text-[#C9B072] transition duration-300">+254 794 255 000</a>
                                </p>
                            </div>
                            <div className="flex justify-center md:justify-start space-x-6">
                                {/* Social Media Icons */}
                                <a href="https://wa.me/254794255000" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9B072] transition duration-300" aria-label="WhatsApp">
                                    <Smartphone size={24} />
                                </a>
                                <a href="https://www.linkedin.com/in/obare-emmanuel-a769672b1" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9B072] transition duration-300" aria-label="LinkedIn">
                                    <Linkedin size={24} />
                                </a>
                                <a href="https://www.facebook.com/emmanuel.jectone/" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9B072] transition duration-300" aria-label="Facebook">
                                    <Facebook size={24} />
                                </a>
                                <a href="https://www.instagram.com/emmanueljectone/" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9B072] transition duration-300" aria-label="Instagram">
                                    <Instagram size={24} />
                                </a>
                                <a href="https://github.com/yourusername" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9B072] transition duration-300" aria-label="GitHub">
                                    <Code size={24} />
                                </a>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </Router>
    );
};

export default App;
